<template>
  <!--
    Med: data-state 把"是否为空"显式标到根节点。
    父级 excel-tag-tab__preview 需要根据这个状态切换 flex 行为:
    empty 时整面板只占自然高(把空间让给 Excel 配置区),
    ready 时整面板 flex:1 撑满剩余高度(接管表格纵向滚动)。
    之前把 empty 写成 flex:1+min-height:120px,会让空态撑成"白板",
    把 mode/上传区挤出弹窗视口,看起来像"死的"。
  -->
  <section
    class="tag-preview-panel"
    :class="[
      { 'tag-preview-panel--overlay': overlay },
      `tag-preview-panel--${state}`,
    ]"
    :data-state="state"
  >
    <div v-if="!overlay" class="tag-preview-panel__head">
      <span class="tag-preview-panel__title">变更预览</span>
      <span v-if="rows.length" class="tag-preview-panel__meta">共 {{ rows.length }} 条</span>
    </div>

    <div v-if="loading" class="tag-preview-panel__loading">
      <i class="el-icon-loading" aria-hidden="true" />
      <span>正在生成预览…</span>
    </div>

    <div v-else-if="!rows.length" class="tag-preview-panel__empty">
      <i class="el-icon-document tag-preview-panel__emptyIcon" aria-hidden="true" />
      <span>{{ emptyHint }}</span>
    </div>

    <template v-else>
      <el-alert
        v-if="clearWarnings.length"
        type="error"
        :closable="false"
        show-icon
        class="tag-preview-panel__alert"
        title="覆盖模式下，以下资源 tag 为空，提交后将清空其全部 Tag"
      >
        <div v-for="row in clearWarnings" :key="row.resourceKey">
          · {{ row.title || '未命名资源' }}
          <span v-if="row.resourceType" class="tag-preview-panel__resourceType">
            （{{ row.resourceType }}）
          </span>
        </div>
      </el-alert>

      <div class="tag-preview-panel__tableWrap">
        <!--
          Med: max-height 按 overlay 区分。
          - overlay=true(页面打标 bottom sheet):max-height="100%" 让表格在底 sheet 内出滚动条,
            与 flex:1 撑满底 sheet 配合,这是历史行为,保留以避免页面打标路径回归。
          - overlay=false(Excel 打标内嵌):不传 max-height,el-table 按行数自然撑大,
            行少→矮、行多→高;弹窗装不下时由外层 excel-tag-tab__preview (overflow:auto) 兜底。
        -->
        <el-table
          :data="rows"
          size="mini"
          border
        >
          <el-table-column prop="title" label="资源名称" min-width="140" />
          <el-table-column prop="resourceType" label="类型" min-width="100" />
          <el-table-column label="Tag 变更" min-width="260">
            <template slot-scope="{ row }">
              <div class="tag-diff">
                <template v-if="row.willClear">
                  <el-tag size="mini" type="danger" class="tag-diff__chip">
                    将清空全部 Tag
                  </el-tag>
                </template>
                <template v-else-if="!row.hasChange">
                  <span class="tag-diff__unchanged">无变化</span>
                </template>
                <template v-else>
                  <el-tag
                    v-for="name in row.keptTags"
                    :key="`k-${row.resourceKey}-${name}`"
                    size="mini"
                    type="info"
                    class="tag-diff__chip"
                  >
                    {{ name }}
                  </el-tag>
                  <el-tag
                    v-for="name in row.addedTags"
                    :key="`a-${row.resourceKey}-${name}`"
                    size="mini"
                    type="success"
                    class="tag-diff__chip tag-diff__chip--added"
                  >
                    +{{ name }}
                  </el-tag>
                  <el-tag
                    v-for="name in row.removedTags"
                    :key="`r-${row.resourceKey}-${name}`"
                    size="mini"
                    type="danger"
                    class="tag-diff__chip tag-diff__chip--removed"
                  >
                    -{{ name }}
                  </el-tag>
                </template>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="tag-diff-legend">
        <span><i class="legend-dot legend-dot--kept" />保留</span>
        <span><i class="legend-dot legend-dot--added" />新增</span>
        <span><i class="legend-dot legend-dot--removed" />移除</span>
      </div>
    </template>
  </section>
</template>

<script>
export default {
  name: 'TagPreviewPanel',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    clearWarnings: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyHint: {
      type: String,
      default: '完成上方配置后将自动显示变更预览',
    },
    /** 嵌入弹窗 overlay 时去掉外层标题与边框，由壳层提供 */
    overlay: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    /**
     * 面板状态机,驱动根节点 class 与 data-state。
     * - loading: 数据生成中,占满剩余高度给用户"在算"反馈
     * - empty:   无数据,只占自然高(标题+一行提示),把空间让给 Excel 配置区
     * - ready:   有数据,flex:1 撑满剩余高度,表格内纵向滚动
     */
    state() {
      if (this.loading) return 'loading'
      if (!this.rows.length) return 'empty'
      return 'ready'
    },
  },
}
</script>

<style scoped>
/*
 * Med: flex 行为按 overlay 拆两条路径。
 *
 * 1) 默认 (Excel 打标内嵌):
 *    根用 height: 100% 占满父级(excel-tag-tab__preview)的剩余高度,
 *    内部把"图例"作为 flex-shrink:0 钉在底部,
 *    "表格容器 __tableWrap" 接管纵向滚动 (flex:1; min-height:0; overflow:auto)。
 *    el-table 在 __tableWrap 内按行数自然撑大:行少则矮、行多则高。
 *    弹窗装不下时滚动条出现在 __tableWrap 边界,
 *    图例始终固定可见,不再被表格挤出视口。
 *    横向滚动由 el-table 自身处理,与纵向解耦。
 *
 * 2) --overlay (页面打标 bottom sheet):
 *    根 flex:1 1 auto 撑满底 sheet 高度,
 *    el-table 动态绑 max-height="100%" 让表格在底 sheet 内出滚动条。
 *    这是历史行为,本次未改动,避免影响页面打标路径。
 */
.tag-preview-panel {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-height: 0;
  height: 100%;
  gap: 8px;
  padding: 10px 12px 8px;
  border-radius: 8px;
  background: var(--link-surface-muted, #f7f4ef);
  border: 1px solid var(--link-line-strong, #e5dfd6);
  box-sizing: border-box;
  margin-bottom: 20px;
}

/*
 * Med: 空态保持自然高度(只占标题+一行提示),
 * 不再 flex:1 抢空间,避免把 mode 单选/上传区挤出弹窗视口。
 */
.tag-preview-panel--empty {
  flex: 0 0 auto;
  height: auto;
}

.tag-preview-panel--loading {
  flex: 0 0 auto;
}

.tag-preview-panel--overlay {
  flex: 1 1 auto;
  height: 100%;
  padding: 8px 12px 10px;
  border: none;
  border-radius: 0;
  background: transparent;
}

.tag-preview-panel__head {
  display: flex;
  flex-shrink: 0;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.tag-preview-panel__title {
  font-size: var(--link-text-md);
  font-weight: var(--link-font-semibold);
  color: var(--link-ink);
}

.tag-preview-panel__meta {
  font-size: var(--link-text-xs);
  color: var(--link-ink-tertiary);
}

.tag-preview-panel__loading {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: var(--link-text-md);
  color: var(--link-ink-muted);
}

/*
 * Med: 空态单独拆出来,不再 flex:1 + min-height:120px。
 * 只用自然高度一行提示,避免把整面板撑成"白板"。
 * 加一个 icon 增强可读性,与加载态视觉区分。
 */
.tag-preview-panel__empty {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0 0;
  font-size: var(--link-text-sm);
  color: var(--link-ink-muted);
  line-height: 1.6;
}

.tag-preview-panel__emptyIcon {
  font-size: var(--link-icon-md, 14px);
  color: var(--link-ink-faint, #c8c2b8);
}

.tag-preview-panel__alert {
  flex-shrink: 0;
}

.tag-preview-panel__resourceType {
  color: var(--link-ink-muted);
  font-size: var(--link-text-sm);
}

.tag-preview-panel__tableWrap {
  /*
   * Med: 接管纵向滚动 + 为图例让位。
   * flex:1 + min-height:0 让本容器占满父级除图例外的全部高度,
   * overflow:auto 在内容超出时出滚动条。
   * el-table 在这里按行数自然撑大(仍不设 max-height),
   * 行少时整体矮(无需滚动条),行多时被本容器边界裁剪并出滚动条。
   * 与图例配合:图例永远在底部一行固定可见,表格独立滚动。
   */
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.tag-diff {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  line-height: 1.6;
  /*
   * Med: 限制 max-width 让超长行不至于把 el-table cell 撑爆。
   * 单行 chip 过多时优先换行（flex-wrap），
   * 但任一 chip 名过长（例如路径类 Tag）时仍允许横向滚动兜底，
   * 避免浏览器/字号差异导致 chip 被截断看不到完整名。
   */
  max-width: 100%;
  overflow-x: auto;
}

.tag-diff__chip {
  margin: 0;
}

.tag-diff__chip--added {
  font-weight: var(--link-font-semibold);
}

.tag-diff__chip--removed {
  text-decoration: line-through;
}

.tag-diff__unchanged {
  color: var(--link-ink-muted);
  font-size: var(--link-text-sm);
}

.tag-diff-legend {
  flex-shrink: 0;
  display: flex;
  gap: 16px;
  font-size: var(--link-text-sm);
  color: var(--link-ink-secondary);
}

.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}

.legend-dot--kept {
  background: var(--link-ink-muted);
}

.legend-dot--added {
  background: var(--link-status-success-ink);
}

.legend-dot--removed {
  background: var(--link-status-error-ink);
}
</style>
